/**
 * Create/edit form for knowledge base categories.
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, Input, Switch, Button, Card, Tabs, Space, message } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useKBCategories, useCreateCategory, useUpdateCategory } from '../hooks/useKnowledgeBase';
import { LanguageSettingsControl } from '../components/knowledgeBase';
import { DEFAULT_LANGUAGE_SETTINGS, validateLanguageSettings, sanitizeLanguageSettings } from '@municipality/types';
import { AVAILABLE_LANGUAGES } from '@municipality/types';
import type { KBCategoryTranslation } from '@municipality/types';

const { TextArea } = Input;

type CategoryFormData = {
  translations: Record<string, { name: string; description?: string }>;
  languageSettings: {
    primaryLanguage: string;
    secondaryLanguages: string[];
    isMultilingual: boolean;
  };
  order: number;
  isActive: boolean;
  showOnLanding: boolean;
};

const schema = yup.object({
  translations: yup
    .object()
    .transform((val) => {
      if (!val || typeof val !== 'object') return val;
      const out: Record<string, { name: string; description?: string }> = {};
      for (const [lang, v] of Object.entries(val)) {
        if (v && typeof v === 'object' && v !== null) {
          const entry = v as { name?: string; description?: string };
          out[lang] = {
            name: entry.name ?? '',
            description: (entry.description && String(entry.description).trim()) || undefined,
          };
        }
      }
      return out;
    })
    .test(
      'primary-name-required',
      'Name is required in primary language',
      function (value) {
        const parent = this.parent as CategoryFormData;
        const primaryLang = parent?.languageSettings?.primaryLanguage;
        if (!value || typeof value !== 'object' || !primaryLang) return false;
        const tr = value as Record<string, { name?: string }>;
        const primary = tr[primaryLang]?.name?.trim();
        return !!primary;
      }
    ),
  languageSettings: yup.object({
    primaryLanguage: yup.string().required(),
    secondaryLanguages: yup.array().of(yup.string()).default([]),
    isMultilingual: yup.boolean().default(false),
  }),
  order: yup.number().required(),
  isActive: yup.boolean().required(),
  showOnLanding: yup.boolean().required(),
});

export const KnowledgeBaseCategoryForm: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;

  const { data: categories, isLoading } = useKBCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();

  const category = useMemo(
    () => categories?.find((c) => c.id === id),
    [categories, id]
  );

  const { control, handleSubmit, watch, setValue, getValues, formState } = useForm<CategoryFormData>({
    resolver: yupResolver(schema) as Resolver<CategoryFormData>,
    defaultValues: {
      translations: { en: { name: '', description: '' } },
      languageSettings: DEFAULT_LANGUAGE_SETTINGS,
      order: 0,
      isActive: true,
      showOnLanding: false,
    },
  });

  const languageSettings = watch('languageSettings');

  useEffect(() => {
    if (category) {
      setValue('translations', category.translations as Record<string, { name: string; description?: string }>);
      setValue('languageSettings', category.languageSettings);
      setValue('order', category.order);
      setValue('isActive', category.isActive);
      setValue('showOnLanding', category.showOnLanding);
    } else if (!isCreate && !isLoading && !categories?.some((c) => c.id === id)) {
      message.error(t('knowledgeBase:errors.loadError'));
    } else if (isCreate && categories?.length) {
      const maxOrder = Math.max(-1, ...categories.map((c) => c.order));
      setValue('order', maxOrder + 1);
    }
  }, [category, isCreate, isLoading, categories, id, setValue, t]);

  useEffect(() => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const langs = [primary, ...secondary.filter((l) => l !== primary)];
    const prev = getValues('translations') as Record<string, { name: string; description?: string }>;
    const next = { ...prev };
    let changed = false;
    langs.forEach((lang) => {
      if (!next[lang]) {
        next[lang] = { name: '', description: '' };
        changed = true;
      }
    });
    if (changed) setValue('translations', next);
  }, [languageSettings?.primaryLanguage, languageSettings?.secondaryLanguages, setValue, getValues]);

  const getTabItems = (): { key: string; label: string; children: React.ReactNode }[] => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const items: { key: string; label: string; children: React.ReactNode }[] = [];

    const langs = [primary, ...secondary.filter((l) => l !== primary)];
    langs.forEach((langCode) => {
      const opt = AVAILABLE_LANGUAGES.find((l) => l.code === langCode);
      items.push({
        key: langCode,
        label: opt?.nativeName ?? langCode,
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Controller
              name={`translations.${langCode}.name`}
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={t('knowledgeBase:categories.name')}
                  required
                  validateStatus={formState.errors.translations ? 'error' : undefined}
                  help={
                    formState.errors.translations?.message
                      ? String(formState.errors.translations.message)
                      : undefined
                  }
                >
                  <Input {...field} placeholder={t('knowledgeBase:categories.name')} />
                </Form.Item>
              )}
            />
            <Controller
              name={`translations.${langCode}.description`}
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.description')} required={false}>
                  <TextArea {...field} rows={3} placeholder={t('knowledgeBase:categories.description')} />
                </Form.Item>
              )}
            />
          </Space>
        ),
      });
    });

    if (items.length === 0) {
      items.push({
        key: 'en',
        label: 'English',
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Controller
              name="translations.en.name"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.name')} required>
                  <Input {...field} placeholder={t('knowledgeBase:categories.name')} />
                </Form.Item>
              )}
            />
            <Controller
              name="translations.en.description"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.description')} required={false}>
                  <TextArea {...field} rows={3} placeholder={t('knowledgeBase:categories.description')} />
                </Form.Item>
              )}
            />
          </Space>
        ),
      });
    }
    return items;
  };

  const onSubmit = async (data: CategoryFormData): Promise<void> => {
    try {
      const validated = validateLanguageSettings(data.languageSettings);
      if (!validated.valid) {
        message.error(validated.errors.join(', '));
        return;
      }
      const langSettings = sanitizeLanguageSettings(data.languageSettings);

      const translations: Record<string, KBCategoryTranslation> = {};
      Object.entries(data.translations).forEach(([lang, v]) => {
        if (v?.name?.trim()) {
          const desc = v.description;
          translations[lang] = {
            name: v.name.trim(),
            ...(desc != null && String(desc).trim() ? { description: String(desc).trim() } : {}),
          };
        }
      });

      if (Object.keys(translations).length === 0) {
        message.error(t('knowledgeBase:errors.saveError'));
        return;
      }

      if (isCreate) {
        await createMutation.mutateAsync({
          translations,
          languageSettings: langSettings,
          order: data.order,
          isActive: data.isActive,
          showOnLanding: data.showOnLanding,
        });
        message.success(t('knowledgeBase:messages.categoryCreated'));
      } else if (id) {
        await updateMutation.mutateAsync({
          id,
          data: {
            translations,
            languageSettings: langSettings,
            order: data.order,
            isActive: data.isActive,
            showOnLanding: data.showOnLanding,
          },
        });
        message.success(t('knowledgeBase:messages.categoryUpdated'));
      }
      navigate('/knowledge-base/categories');
    } catch {
      message.error(t('knowledgeBase:errors.saveError'));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/knowledge-base/categories')}
        >
          {t('common:buttons.back')}
        </Button>
        <h2 style={{ margin: 0 }}>
          {isCreate ? t('knowledgeBase:categories.create') : t('knowledgeBase:categories.edit')}
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card style={{ marginBottom: 24 }}>
          <LanguageSettingsControl control={control} setValue={setValue} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Tabs items={getTabItems()} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Controller
              name="order"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.order')}>
                  <Input
                    type="number"
                    min={0}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.active')}>
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren={t('common:common.enabled')}
                    unCheckedChildren={t('common:common.disabled')}
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="showOnLanding"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('knowledgeBase:categories.showOnLanding')}>
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren={t('common:common.yes')}
                    unCheckedChildren={t('common:common.no')}
                  />
                </Form.Item>
              )}
            />
          </Space>
        </Card>

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={isPending}
          >
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => navigate('/knowledge-base/categories')}>
            {t('common:buttons.cancel')}
          </Button>
        </Space>
      </form>
    </div>
  );
};
